import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ANALYSIS_MODES } from "../../utils/requestAnalysisMock";
import { patchRequestAnalysis } from "../../utils/adminInboxStore";
import { queueManualAnalysis } from "../../utils/requestAnalysisRunner";
import { isAdminApiEnabled } from "../../config/adminApiFeature";
import { useAppDispatch } from "../../store/hooks";
import {
  queueServiceRequestAnalysis,
  updateServiceRequest,
} from "../../store/slices/serviceRequestsSlice";
import {
  formatInsightsAsPlainText,
  hasAnyInsights,
  normalizeAiInsights,
  TECHNICAL_EFFORT_STEPS,
} from "../../utils/aiAnalysisInsights";
import {
  IconAiModel,
  IconAnalysisClock,
  IconAnalysisSignal,
  IconChevronDown,
  IconInsightGaps,
  IconInsightQuestions,
  IconInsightRisks,
  IconInsightSteps,
  IconPriceEstimate,
} from "./AnalysisInsightSectionIcons";
import { segmentTextByKeywords } from "../../utils/highlightAnalysisKeywords";
import {
  hasModelDetailsToShow,
  normalizeModelAttempts,
  primaryModelId,
  shortModelName,
} from "../../utils/aiAnalysisModelMeta";

/** Split plain-text summary into blocks (blank lines) for readable paragraphs. */
function summaryParagraphs(text) {
  if (!text || typeof text !== "string") return [];
  return text
    .replace(/\r\n/g, "\n")
    .split(/\n(?:\s*\n)+/)
    .map((p) => p.trim())
    .filter(Boolean);
}

/** Renders `text` with `keywords` wrapped in <strong>. */
function HighlightedLine({ text, keywords }) {
  const segs = segmentTextByKeywords(text, keywords || []);
  return segs.map((s, i) =>
    s.hit ? (
      <strong key={i} className="admin-dash__analysis-keyword-em">
        {s.text}
      </strong>
    ) : (
      <span key={i}>{s.text}</span>
    )
  );
}

function statusMeta(status) {
  switch (status) {
    case "pending":
      return { label: "Analysing", className: "admin-dash__analysis-badge admin-dash__analysis-badge--pending" };
    case "ok":
      return { label: "Ready", className: "admin-dash__analysis-badge admin-dash__analysis-badge--ok" };
    case "error":
      return { label: "Failed", className: "admin-dash__analysis-badge admin-dash__analysis-badge--error" };
    default:
      return { label: "Not started", className: "admin-dash__analysis-badge admin-dash__analysis-badge--none" };
  }
}

/** For collapsible chrome / badges outside this panel */
export function analysisChromeMeta(status) {
  return statusMeta(status);
}

export default function RequestAnalysisPanel({ requestId, analysis, embedInCollapsible = false }) {
  const apiOn = isAdminApiEnabled();
  const dispatch = useAppDispatch();
  const ai = useMemo(() => analysis || {}, [analysis]);
  const status = ai.status || "none";
  const meta = statusMeta(status);
  const [instruction, setInstruction] = useState(ai.instructionNote || "");

  useEffect(() => {
    setInstruction(ai.instructionNote || "");
  }, [requestId, ai.instructionNote]);

  const saveInstruction = useCallback(() => {
    const note = instruction.trim();
    if (apiOn) {
      dispatch(
        updateServiceRequest({
          id: requestId,
          body: {
            aiAnalysis: {
              ...ai,
              instructionNote: note,
            },
          },
        })
      );
    } else {
      patchRequestAnalysis(requestId, { instructionNote: note });
    }
  }, [ai, apiOn, dispatch, instruction, requestId]);

  const runLocal = useCallback(
    (detailed) => {
      queueManualAnalysis(requestId, {
        mode: detailed ? ANALYSIS_MODES.detailed : ANALYSIS_MODES.standard,
        note: instruction,
      });
    },
    [instruction, requestId]
  );

  const runApi = useCallback(
    (detailed) => {
      const base = instruction.trim();
      const instructionNote = detailed
        ? `${base}${base ? "\n\n" : ""}Please provide a more detailed technical breakdown.`.trim()
        : base || undefined;
      dispatch(queueServiceRequestAnalysis({ id: requestId, instructionNote }));
    },
    [dispatch, instruction, requestId]
  );

  const runStandard = useCallback(() => {
    if (apiOn) runApi(false);
    else runLocal(false);
  }, [apiOn, runApi, runLocal]);

  const runDetailed = useCallback(() => {
    if (apiOn) runApi(true);
    else runLocal(true);
  }, [apiOn, runApi, runLocal]);

  const runs = Array.isArray(ai.runs) ? [...ai.runs].reverse() : [];
  const resolvedModelId = primaryModelId(ai);
  const resolvedModelShort = shortModelName(resolvedModelId);
  const modelAttempts = normalizeModelAttempts(ai);
  const showModelDetailsBlock = hasModelDetailsToShow(ai);
  const insights = normalizeAiInsights(ai);
  const primarySummary = typeof ai.summary === "string" ? ai.summary.trim() : "";
  const fallbackFromInsights =
    !primarySummary && hasAnyInsights(insights) ? formatInsightsAsPlainText(insights) : "";
  const cardProse = primarySummary || fallbackFromInsights;
  const summaryBlocks = summaryParagraphs(cardProse);
  const hasListSections =
    insights.missingInfo.length > 0 ||
    insights.clarifyingQuestions.length > 0 ||
    insights.risks.length > 0 ||
    insights.nextSteps.length > 0;
  const hasEffortInsightData = Boolean(insights.technicalEffort || insights.effortBand);
  const showEffortPanel = status !== "pending" && hasEffortInsightData;
  const showPricingPanel = status !== "pending" && Boolean(insights.price?.hasPrice);
  const showInsightTiles = status !== "pending" && hasListSections;
  const showAnalysisBreakdown = showEffortPanel || showPricingPanel || showInsightTiles;
  const showEffortChipInSummaryFooter =
    Boolean(primarySummary && insights.effortBand) && !showEffortPanel;

  const toolbar = (
    <div className="admin-dash__analysis-studio-toolbar">
      {resolvedModelId ? (
        <span className="admin-dash__analysis-studio-model-chip" title={resolvedModelId}>
          <IconAiModel width={15} height={15} strokeWidth={2} aria-hidden />
          {resolvedModelShort}
        </span>
      ) : null}
      <span className={meta.className}>{meta.label}</span>
    </div>
  );

  return (
    <section
      className={`admin-dash__analysis-studio${embedInCollapsible ? " admin-dash__analysis-studio--embed" : ""}`}
      aria-label="Request analysis"
    >
      <div className="admin-dash__analysis-studio-inner">
        {embedInCollapsible ? (
          <>
            <div className="admin-dash__analysis-studio-embed-head">
              <p className="admin-dash__analysis-studio-eyebrow">AI insight workspace</p>
              {toolbar}
            </div>
            <p className="admin-dash__analysis-studio-embed-lede">
              {apiOn
                ? "Queue analysis from your worker — results sync via API and live updates."
                : "Local preview only. Enable the admin API to run against your worker."}
            </p>
          </>
        ) : (
          <header className="admin-dash__analysis-studio-head">
            <div className="admin-dash__analysis-studio-brand">
              <span className="admin-dash__analysis-studio-icon-wrap" aria-hidden="true">
                <IconAiModel />
              </span>
              <div className="admin-dash__analysis-studio-brand-text">
                <p className="admin-dash__analysis-studio-eyebrow">AI insight workspace</p>
                <h2 className="admin-dash__analysis-studio-title">Request analysis</h2>
                <p className="admin-dash__analysis-studio-lede">
                  {apiOn
                    ? resolvedModelId
                      ? `Structured output from your worker. Resolved model: ${resolvedModelShort}.`
                      : "Queue analysis from your worker — results arrive via REST and live sockets."
                    : "Local preview only. Enable the admin API to run against your worker and models."}
                </p>
              </div>
            </div>
            {toolbar}
          </header>
        )}

        {showModelDetailsBlock ? (
          <details className="admin-dash__analysis-model-details">
            <summary className="admin-dash__analysis-model-details-summary">
              <IconChevronDown className="admin-dash__analysis-model-details-chevron" aria-hidden />
              <span className="admin-dash__analysis-model-details-summary-main">Model &amp; attempts</span>
              <span className="admin-dash__analysis-model-details-summary-hint">
                Open to see routing, fallbacks, and quota messages
              </span>
            </summary>
            <div className="admin-dash__analysis-model-details-panel">
              {resolvedModelId ? (
                <p className="admin-dash__analysis-model-resolved">
                  <strong>Resolved model</strong>{" "}
                  <code className="admin-dash__code-inline admin-dash__code-inline--blocky">{resolvedModelId}</code>
                </p>
              ) : null}
              {modelAttempts.length > 0 ? (
                <ul className="admin-dash__analysis-attempt-list" aria-label="Model attempt log">
                  {modelAttempts.map((att, i) => (
                    <li
                      key={`${att.provider}-${att.model}-${i}`}
                      className={`admin-dash__analysis-attempt-item${att.ok ? " admin-dash__analysis-attempt-item--ok" : " admin-dash__analysis-attempt-item--fail"}`}
                    >
                      <span className="admin-dash__analysis-attempt-glyph" aria-hidden="true" />
                      <div className="admin-dash__analysis-attempt-body">
                        <div className="admin-dash__analysis-attempt-top">
                          <span className="admin-dash__analysis-attempt-provider">{att.provider}</span>
                          <code className="admin-dash__analysis-attempt-model">{att.model}</code>
                          <span
                            className={
                              att.ok
                                ? "admin-dash__analysis-attempt-status admin-dash__analysis-attempt-status--ok"
                                : "admin-dash__analysis-attempt-status admin-dash__analysis-attempt-status--fail"
                            }
                          >
                            {att.ok ? "Used" : "Skipped"}
                          </span>
                        </div>
                        {att.detail ? (
                          <p className="admin-dash__analysis-attempt-detail">{att.detail}</p>
                        ) : null}
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="admin-dash__analysis-model-empty">
                  No <code className="admin-dash__code-inline">modelAttempts</code> array in this payload yet.
                </p>
              )}
            </div>
          </details>
        ) : null}

        {status === "pending" ? (
          <p className="admin-dash__analysis-pending">
            <span className="admin-dash__analysis-spinner" aria-hidden="true" />
            {apiOn
              ? "Analysis is running on the server… wait for live updates or refresh."
              : "Calling model… this is simulated locally (≈1s)."}
          </p>
        ) : null}

      {status === "error" && ai.error ? (
        <div className="admin-dash__analysis-error" role="alert">
          <strong>Last error</strong>
          <p>{ai.error}</p>
        </div>
      ) : null}

      {cardProse ? (
        <article className="admin-dash__analysis-summary-card" aria-labelledby="analysis-summary-heading">
          <header className="admin-dash__analysis-summary-head">
            <div className="admin-dash__analysis-summary-icon" aria-hidden="true">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.847a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.847.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456Z"
                />
              </svg>
            </div>
            <div className="admin-dash__analysis-summary-title-block">
              <p className="admin-dash__analysis-summary-eyebrow">AI output</p>
              <h3 className="admin-dash__analysis-summary-title" id="analysis-summary-heading">
                Current summary
              </h3>
              {resolvedModelId ? (
                <p className="admin-dash__analysis-summary-model-ref" title={resolvedModelId}>
                  <IconAiModel width={14} height={14} strokeWidth={2} aria-hidden />
                  <span>{resolvedModelShort}</span>
                </p>
              ) : null}
            </div>
          </header>
          {insights.keywords.length > 0 ? (
            <div className="admin-dash__analysis-keywords-bar">
              <span className="admin-dash__analysis-keywords-label">Keywords</span>
              <ul className="admin-dash__analysis-keywords-chips" aria-label="Extracted keywords">
                {insights.keywords.map((kw) => (
                  <li key={kw.toLowerCase()}>
                    <span className="admin-dash__analysis-keyword-chip">
                      <strong className="admin-dash__analysis-keyword-chip-text">{kw}</strong>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          <div className="admin-dash__analysis-summary-scroll">
            <div className="admin-dash__analysis-summary-prose">
              {summaryBlocks.map((block, i) => (
                <p key={i} className="admin-dash__analysis-summary-p">
                  {block.split("\n").map((line, j) => (
                    <React.Fragment key={j}>
                      {j > 0 ? <br /> : null}
                      <HighlightedLine text={line} keywords={insights.keywords} />
                    </React.Fragment>
                  ))}
                </p>
              ))}
            </div>
          </div>
          {(ai.updatedAt || typeof ai.attempts === "number" || showEffortChipInSummaryFooter) && (
            <footer className="admin-dash__analysis-summary-footer">
              {ai.updatedAt ? (
                <span className="admin-dash__analysis-summary-chip">
                  <span className="admin-dash__analysis-summary-chip-k">Updated</span>
                  <time className="admin-dash__analysis-summary-chip-v" dateTime={ai.updatedAt}>
                    {new Date(ai.updatedAt).toLocaleString()}
                  </time>
                </span>
              ) : null}
              {typeof ai.attempts === "number" ? (
                <span className="admin-dash__analysis-summary-chip admin-dash__analysis-summary-chip--muted">
                  <span className="admin-dash__analysis-summary-chip-k">Runs</span>
                  <span className="admin-dash__analysis-summary-chip-v">
                    {ai.attempts} attempt{ai.attempts === 1 ? "" : "s"}
                  </span>
                </span>
              ) : null}
              {showEffortChipInSummaryFooter ? (
                <span
                  className="admin-dash__analysis-summary-chip admin-dash__analysis-summary-chip--effort"
                  title="Indicative only — confirm in discovery"
                >
                  <span className="admin-dash__analysis-summary-chip-k">Effort / timeline</span>
                  <span className="admin-dash__analysis-summary-chip-v">{insights.effortBand}</span>
                </span>
              ) : null}
            </footer>
          )}
        </article>
      ) : status !== "pending" ? (
        <p className="admin-dash__td-muted">No successful summary yet. Run an analysis below.</p>
      ) : null}

      {showAnalysisBreakdown ? (
        <div className="admin-dash__analysis-breakdown" aria-label="Structured analysis breakdown">
          {showEffortPanel ? (
            <section className="admin-dash__analysis-effort-card" aria-labelledby="analysis-effort-heading">
              <header className="admin-dash__analysis-effort-head">
                <div className="admin-dash__analysis-effort-head-icons" aria-hidden="true">
                  <span className="admin-dash__analysis-effort-clock">
                    <IconAnalysisClock />
                  </span>
                  <span className="admin-dash__analysis-effort-signal">
                    <IconAnalysisSignal />
                  </span>
                </div>
                <div className="admin-dash__analysis-effort-head-copy">
                  <p className="admin-dash__analysis-effort-eyebrow">Technical outlook</p>
                  <h3 className="admin-dash__analysis-effort-title" id="analysis-effort-heading">
                    Effort &amp; complexity
                  </h3>
                  <p className="admin-dash__analysis-effort-lede">
                    Indicative ratings from the model — use them to triage, not as a fixed quote.
                  </p>
                </div>
              </header>

              {insights.technicalEffort ? (
                <div
                  className="admin-dash__analysis-effort-scale"
                  role="group"
                  aria-label="Technical difficulty"
                >
                  {TECHNICAL_EFFORT_STEPS.map((step, idx) => {
                    const active = insights.technicalEffort === step.id;
                    return (
                      <div key={step.id} className="admin-dash__analysis-effort-scale-item">
                        {idx > 0 ? (
                          <span className="admin-dash__analysis-effort-connector" aria-hidden="true" />
                        ) : null}
                        <div
                          className={`admin-dash__analysis-effort-pill admin-dash__analysis-effort-pill--${step.id}${
                            active ? " admin-dash__analysis-effort-pill--active" : ""
                          }`}
                          aria-current={active ? "true" : undefined}
                        >
                          <span className="admin-dash__analysis-effort-pill-label">{step.label}</span>
                          <span className="admin-dash__analysis-effort-pill-hint">{step.hint}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : null}

              {insights.effortBand ? (
                <div className="admin-dash__analysis-effort-timeline">
                  <span className="admin-dash__analysis-effort-timeline-label">
                    <IconAnalysisClock width={16} height={16} strokeWidth={2} />
                    Timeline &amp; scope note
                  </span>
                  <p className="admin-dash__analysis-effort-timeline-text">
                    <HighlightedLine text={insights.effortBand} keywords={insights.keywords} />
                  </p>
                </div>
              ) : insights.technicalEffort ? null : (
                <p className="admin-dash__analysis-effort-fallback">
                  No timeline note for this run. Your worker can set <code className="admin-dash__code-inline">effortBand</code> for duration or scope hints.
                </p>
              )}
            </section>
          ) : null}

          {showPricingPanel ? (
            <section className="admin-dash__analysis-pricing-card" aria-labelledby="analysis-pricing-heading">
              <header className="admin-dash__analysis-pricing-head">
                <span className="admin-dash__analysis-pricing-icon" aria-hidden="true">
                  <IconPriceEstimate />
                </span>
                <div className="admin-dash__analysis-pricing-head-copy">
                  <p className="admin-dash__analysis-pricing-eyebrow">Indicative budget</p>
                  <h3 className="admin-dash__analysis-pricing-title" id="analysis-pricing-heading">
                    Suggested price range
                  </h3>
                  <p className="admin-dash__analysis-pricing-disclaimer">
                    Model-generated ballpark only — not a quote, not binding. Confirm with your commercial process and
                    discovery.
                  </p>
                </div>
              </header>
              <div className="admin-dash__analysis-pricing-body">
                <p className="admin-dash__analysis-pricing-amount">
                  <HighlightedLine
                    text={insights.price.displayLine}
                    keywords={insights.keywords}
                  />
                </p>
                {insights.price.detail ? (
                  <p className="admin-dash__analysis-pricing-note">
                    <HighlightedLine text={insights.price.detail} keywords={insights.keywords} />
                  </p>
                ) : null}
              </div>
            </section>
          ) : null}

          {showInsightTiles ? (
            <div className="admin-dash__analysis-tiles">
              {insights.missingInfo.length > 0 ? (
                <section
                  className="admin-dash__analysis-tile admin-dash__analysis-tile--gaps"
                  aria-labelledby="tile-gaps"
                >
                  <header className="admin-dash__analysis-tile-head">
                    <span className="admin-dash__analysis-tile-icon-wrap" aria-hidden="true">
                      <IconInsightGaps />
                    </span>
                    <div className="admin-dash__analysis-tile-head-text">
                      <h4 className="admin-dash__analysis-tile-title" id="tile-gaps">
                        Missing or unclear information
                      </h4>
                      <p className="admin-dash__analysis-tile-sub">Fill these gaps before estimating or replying.</p>
                    </div>
                  </header>
                  <ul className="admin-dash__analysis-tile-list">
                    {insights.missingInfo.map((line, i) => (
                      <li key={i} className="admin-dash__analysis-tile-li">
                        <span className="admin-dash__analysis-tile-li-dot" aria-hidden="true" />
                        <span>
                          <HighlightedLine text={line} keywords={insights.keywords} />
                        </span>
                      </li>
                    ))}
                  </ul>
                </section>
              ) : null}

              {insights.clarifyingQuestions.length > 0 ? (
                <section
                  className="admin-dash__analysis-tile admin-dash__analysis-tile--questions"
                  aria-labelledby="tile-questions"
                >
                  <header className="admin-dash__analysis-tile-head">
                    <span className="admin-dash__analysis-tile-icon-wrap" aria-hidden="true">
                      <IconInsightQuestions />
                    </span>
                    <div className="admin-dash__analysis-tile-head-text">
                      <h4 className="admin-dash__analysis-tile-title" id="tile-questions">
                        Clarifying questions
                      </h4>
                      <p className="admin-dash__analysis-tile-sub">Ask the client to unblock the brief.</p>
                    </div>
                  </header>
                  <ol className="admin-dash__analysis-tile-list admin-dash__analysis-tile-list--ol">
                    {insights.clarifyingQuestions.map((line, i) => (
                      <li key={i} className="admin-dash__analysis-tile-li">
                        <span>
                          <HighlightedLine text={line} keywords={insights.keywords} />
                        </span>
                      </li>
                    ))}
                  </ol>
                </section>
              ) : null}

              {insights.risks.length > 0 ? (
                <section
                  className="admin-dash__analysis-tile admin-dash__analysis-tile--risks"
                  aria-labelledby="tile-risks"
                >
                  <header className="admin-dash__analysis-tile-head">
                    <span className="admin-dash__analysis-tile-icon-wrap" aria-hidden="true">
                      <IconInsightRisks />
                    </span>
                    <div className="admin-dash__analysis-tile-head-text">
                      <h4 className="admin-dash__analysis-tile-title" id="tile-risks">
                        Risks and constraints
                      </h4>
                      <p className="admin-dash__analysis-tile-sub">Watch these during discovery and delivery.</p>
                    </div>
                  </header>
                  <ul className="admin-dash__analysis-tile-list">
                    {insights.risks.map((line, i) => (
                      <li key={i} className="admin-dash__analysis-tile-li">
                        <span className="admin-dash__analysis-tile-li-dot admin-dash__analysis-tile-li-dot--risk" aria-hidden="true" />
                        <span>
                          <HighlightedLine text={line} keywords={insights.keywords} />
                        </span>
                      </li>
                    ))}
                  </ul>
                </section>
              ) : null}

              {insights.nextSteps.length > 0 ? (
                <section
                  className="admin-dash__analysis-tile admin-dash__analysis-tile--steps"
                  aria-labelledby="tile-steps"
                >
                  <header className="admin-dash__analysis-tile-head">
                    <span className="admin-dash__analysis-tile-icon-wrap" aria-hidden="true">
                      <IconInsightSteps />
                    </span>
                    <div className="admin-dash__analysis-tile-head-text">
                      <h4 className="admin-dash__analysis-tile-title" id="tile-steps">
                        Suggested next steps
                      </h4>
                      <p className="admin-dash__analysis-tile-sub">Practical follow-ups for your team.</p>
                    </div>
                  </header>
                  <ul className="admin-dash__analysis-tile-list">
                    {insights.nextSteps.map((line, i) => (
                      <li key={i} className="admin-dash__analysis-tile-li">
                        <span className="admin-dash__analysis-tile-li-dot admin-dash__analysis-tile-li-dot--step" aria-hidden="true" />
                        <span>
                          <HighlightedLine text={line} keywords={insights.keywords} />
                        </span>
                      </li>
                    ))}
                  </ul>
                </section>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="admin-dash__analysis-actions">
        <label className="admin-dash__label">
          Instructions for the next run (optional)
          <textarea
            className="admin-dash__textarea admin-dash__textarea--compact"
            value={instruction}
            onChange={(e) => setInstruction(e.target.value)}
            rows={3}
            placeholder="e.g. Focus on compliance risks, or compare AWS vs Azure for this workload…"
          />
        </label>
        <div className="admin-dash__analysis-btn-row">
          <button type="button" className="admin-dash__btn-secondary" onClick={saveInstruction}>
            Save note
          </button>
          <button
            type="button"
            className="admin-dash__btn-primary admin-dash__btn-primary--compact"
            disabled={status === "pending"}
            onClick={runStandard}
          >
            {apiOn ? "Queue analysis" : "Re-analyse"}
          </button>
          <button
            type="button"
            className="admin-dash__btn-primary admin-dash__btn-primary--compact"
            disabled={status === "pending"}
            onClick={runDetailed}
          >
            {apiOn ? "Queue detailed run" : "Re-analyse (detailed)"}
          </button>
        </div>
      </div>

      {runs.length > 0 ? (
        <div className="admin-dash__analysis-history admin-dash__analysis-history--timeline">
          <div className="admin-dash__analysis-history-head">
            <h3 className="admin-dash__analysis-history-title">Run history</h3>
            <p className="admin-dash__analysis-history-sub">Newest first — each card is one queued analysis.</p>
          </div>
          <ol className="admin-dash__analysis-timeline">
            {runs.map((run) => {
              const runModelRaw = typeof run.model === "string" ? run.model.trim() : "";
              const runModelShort = runModelRaw ? shortModelName(runModelRaw) : "";
              return (
                <li
                  key={run.id}
                  className={`admin-dash__analysis-timeline-item${run.ok ? "" : " admin-dash__analysis-timeline-item--fail"}`}
                >
                  <span className="admin-dash__analysis-timeline-node" aria-hidden="true" />
                  <article className="admin-dash__analysis-timeline-card">
                    <header className="admin-dash__analysis-timeline-card-head">
                      <time className="admin-dash__analysis-timeline-time" dateTime={run.at}>
                        {new Date(run.at).toLocaleString()}
                      </time>
                      <div className="admin-dash__analysis-timeline-badges">
                        <span className="admin-dash__analysis-timeline-chip admin-dash__analysis-timeline-chip--mode">
                          {run.mode || "—"}
                        </span>
                        {runModelRaw ? (
                          <span
                            className="admin-dash__analysis-timeline-chip admin-dash__analysis-timeline-chip--model"
                            title={runModelRaw}
                          >
                            <IconAiModel width={11} height={11} strokeWidth={2} aria-hidden />
                            {runModelShort}
                          </span>
                        ) : null}
                        <span
                          className={
                            run.ok
                              ? "admin-dash__analysis-timeline-status admin-dash__analysis-timeline-status--ok"
                              : "admin-dash__analysis-timeline-status admin-dash__analysis-timeline-status--fail"
                          }
                        >
                          {run.ok ? "Success" : "Failed"}
                        </span>
                      </div>
                    </header>
                    {run.note ? (
                      <p className="admin-dash__analysis-timeline-note">
                        <span className="admin-dash__analysis-timeline-note-k">Note</span> {run.note}
                      </p>
                    ) : null}
                    {run.ok && run.summary ? (
                      <p className="admin-dash__analysis-timeline-snippet">
                        <HighlightedLine
                          text={
                            run.summary.length > 160 ? `${run.summary.slice(0, 160)}…` : run.summary
                          }
                          keywords={insights.keywords}
                        />
                      </p>
                    ) : null}
                    {!run.ok && run.error ? (
                      <p className="admin-dash__analysis-timeline-err" role="alert">
                        {run.error}
                      </p>
                    ) : null}
                  </article>
                </li>
              );
            })}
          </ol>
        </div>
      ) : null}
      </div>
    </section>
  );
}
